import { defineComponent, h } from 'vue';
import { Icon } from '../icon/Icon';
import { t } from '@/ui/i18n/i18n';
import { CreateUserModal } from '@/ui/components/user/CreateUserModal';
import { canAddUser, canSelfServeSubscription } from '@/ui/auth/auth';
import { SelectOptions } from '@/ui/types';
import { TagField } from '@/ui/components/form';
import { ValidationError } from '@/entities/validation';
import { AddUserSeatsModal } from '@/ui/components/modals/AddUserSeatsModal';
import CannotAddUserSeatsModal from '@/ui/components/modals/CannotAddUserSeatsModal.vue';
import { useModal } from '@/ui/concerns/modals/useModal';

interface ChooseUserFieldProps {
  options: SelectOptions;
  value: number[];
  errors: ValidationError[];
  disabled?: boolean;
  required?: boolean;
  label?: string;
  placeholder?: string;
  identifier?: string;
  hideLabel?: boolean;
  name?: string;
  maxListedOptions?: number;
  hideCreateUser?: boolean;
}

export const ChooseUserField = defineComponent<ChooseUserFieldProps>({
  name: 'ChooseUserField',
  props: ['options', 'value', 'errors', 'label', 'placeholder', 'identifier', 'hideLabel', 'name', 'maxListedOptions',
    'hideCreateUser', 'disabled'],
  setup(props, ctx) {
    const [isAddUserModalOpen, openAddUserModal, closeAddUserModal] = useModal();
    const [isAddUserSeatModalOpen, openAddUserSeatModal, closeAddUserSeatModal] = useModal();

    const onCreateUserClick = () => {
      if (canAddUser()) {
        openAddUserModal();
      } else {
        openAddUserSeatModal();
      }
    };

    const onUserCreated = (value: number) => {
      closeAddUserModal();
      ctx.emit('user-created', value);
    };

    const onUserSeatAdded = () => {
      closeAddUserSeatModal();
      openAddUserModal();
    };

    return function render() {
      return h('div', { class: 'field choose-user-field' }, [
        h(TagField, {
          props: {
            name: props.name ?? 'users',
            value: props.value,
            options: props.options,
            disabled: props.disabled,
            placeholder: props.placeholder ?? t('group.enter-users'),
            label: props.label ?? t('common.users'),
            required: false,
            errors: props.errors,
            hideLabel: props.hideLabel,
            inputName: props.name ?? 'users',
            maxListedOptions: props.maxListedOptions ?? 100,
          },
          on: {
            input: (value: Array<string | number>) => ctx.emit('input', value),
          },
        }, [
          h('template', {
            slot: 'selected',
          }, [
            ctx.slots.selected ? ctx.slots.selected() : null,
          ]),
          h('template', {
            slot: 'header',
          }, [
            !props.hideCreateUser && h('a', { class: 'choose-user-field__header-link', on: { click: onCreateUserClick } }, [
              h(Icon, { props: { icon: 'plus', classes: 'has-text-primary' } }),
              h('span', { class: 'has-text-primary has-text-weight-semibold' }, t('common.create-user')),
            ]),
            ctx.slots.beforeList ? ctx.slots.beforeList() : null,
          ]),
        ]),
        canSelfServeSubscription.value
          ? h(AddUserSeatsModal, {
            props: {
              active: isAddUserSeatModalOpen.value,
            },
            on: {
              close: closeAddUserSeatModal,
              'created:user-seat': onUserSeatAdded,
            },
          })
          : h(CannotAddUserSeatsModal, {
            props: {
              active: isAddUserSeatModalOpen.value,
            },
            on: {
              close: closeAddUserSeatModal,
            },
          }),
        h(CreateUserModal, {
          props: { active: isAddUserModalOpen.value, identifier: props.identifier },
          on: { close: closeAddUserModal, 'user:created': onUserCreated },
        }),
      ]);
    };
  },
});

export function chooseUserField(
  { props, on = {}, classes = '' }: { props: ChooseUserFieldProps; on?: Record<string, Function>; classes?: string },
) {
  return h(ChooseUserField, { props, on, class: classes });
}
